import { Group } from 'three';
import { sampleProfile } from './profileGeometry.js';

// The source assembly's exterior/hinge side is -Z. Inward opening uses +Z.
// Leaf, cassettes, glass, lock and handles share this rigid transform.
export function doorOpeningAxis({ leftMm, rightMm, handing = 'left', opening = 'outward', index = 0, double = false }) {
  const hingeRight = double ? index === 1 : handing === 'right';
  const inward = opening === 'inward';
  return { x: (hingeRight ? rightMm + 1 : leftMm - 1) / 1000,
    z: inward ? 0.0205 : -0.0655,
    sign: (hingeRight ? -1 : 1) * (inward ? -1 : 1),
    active: !double || index === (handing === 'right' ? 1 : 0) };
}

// Lock.glb's faceplate occupies local X = 5.5…8.5 mm; its bolts project toward -X.
// Seat the plate's outer face at the leaf edge, leaving its thickness inside the leaf.
export function doorLockPlacement(edge, direction, mounting) {
  if (!mounting) return { scaleX: -direction, x: edge + direction * 0.0055 };
  const {backMm,edgeOffsetMm,faceplateWidthMm,sideProfileMm}=mounting;
  const low=backMm+edgeOffsetMm, high=low+faceplateWidthMm;
  const profile=sampleProfile(sideProfileMm);
  const insets=[];
  for(let i=1;i<profile.length;i++) {
    const [a,b]=[profile[i-1],profile[i]];
    const lo=Math.max(low,Math.min(a[1],b[1])),hi=Math.min(high,Math.max(a[1],b[1]));
    if(lo>hi) continue;
    for(const z of [lo,hi]) insets.push(Math.abs(b[1]-a[1])<1e-9?Math.max(a[0],b[0]):
      a[0]+(b[0]-a[0])*(z-a[1])/(b[1]-a[1]));
  }
  // Register to the outermost part of the bevel so the plate is visible
  // across its full width. A 0.1 mm reveal avoids coplanar surface flicker.
  const inset=(Math.max(0,insets.length?Math.min(...insets):0)-0.1)/1000;
  return {scaleX:-direction,x:edge-direction*inset+direction*0.0055,z:(low+high)/2000};
}
// Parts are authored in assembly coordinates. Reflect the complete design
// inside this leaf's own width, after UV projection and before hinge motion.
// Hardware is added separately so locks and hinges keep their mounting sides.
export function mirrorLeafDesign(parts, leftMm, rightMm) {
  const group = new Group();
  group.name = 'MirroredDoorLeafDesign';
  group.position.x = (leftMm + rightMm) / 1000;
  group.scale.x = -1;
  parts.forEach(part => group.add(part));
  return group;
}

// Editor coordinates are read from outside. The assembly is viewed from -Z,
// where increasing world X runs left on screen. Reflect the design (including
// glass and cassettes), never the hardware. The second double leaf faces it.
export function orientLeafDesignForExterior(parts, leftMm, rightMm, index = 0, double = false) {
  return double && index === 1 ? null : mirrorLeafDesign(parts, leftMm, rightMm);
}
